import { MIME_TYPES } from '@mantine/dropzone'
import { ChangeEvent, DragEvent, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import Papa from 'papaparse'

import styles from './BulkMarkAsPaidModal.module.scss'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import {
  AcknowledgeModal,
  ActionModal,
  Alert,
  Badge,
  ButtonText,
  Dropzone,
  Flex,
  Modal,
  Spacer,
  Table,
  TableBody,
  TableCell,
  TableProvider,
  TableRow,
  Text
} from '@percent/lemonade'
import { useMutation } from '@percent/utility'
import {
  BulkMarkAsPaidDryRunResponse,
  BulkMarkAsPaidSkippedItem
} from '@percent/admin-dashboard/api/actions/disbursements/disbursements.types'
import { getApiErrorCode } from '@percent/admin-dashboard/common/library/getApiErrorCode'
import { parseChequeReconciliationCsv, ParsedCsvRow } from './parseChequeReconciliationCsv'

const MAX_CSV_SIZE_BYTES = 2 * 1024 * 1024
const ALREADY_PAID_REASON = 'already_paid'
const ERROR_CODE_NO_VALID_ROWS = 'disbursement/bulk_mark_as_paid/no_valid_rows'
const SKIPPED_CSV_FILENAME = 'skipped-disbursements.csv'
const MODAL_WIDTH_STANDARD = 560
const MODAL_WIDTH_WIDE = 960

interface BulkMarkAsPaidModalProps {
  onClose: () => void
  refresh?: (() => void) | null
}

type PreviewTableRow = ParsedCsvRow & {
  status: 'paid' | 'already_paid' | 'skipped'
  reason?: string
}

type ModalView = 'upload' | 'previewing' | 'preview' | 'processing' | 'success' | 'error'

const SKIP_REASON_I18N: ReadonlyArray<{ match: 'exact' | 'prefix'; key: string; i18n: string }> = [
  { match: 'exact', key: ALREADY_PAID_REASON, i18n: 'dialog.bulkMarkAsPaid.skipReason.alreadyPaid' },
  { match: 'exact', key: 'not_found', i18n: 'dialog.bulkMarkAsPaid.skipReason.notFound' },
  { match: 'exact', key: 'not_cheque_type', i18n: 'dialog.bulkMarkAsPaid.skipReason.notChequeType' },
  { match: 'exact', key: 'invalid_disbursement_id', i18n: 'dialog.bulkMarkAsPaid.skipReason.invalidDisbursementId' },
  { match: 'prefix', key: 'unexpected_status', i18n: 'dialog.bulkMarkAsPaid.skipReason.unexpectedStatus' },
  { match: 'prefix', key: 'amount_mismatch', i18n: 'dialog.bulkMarkAsPaid.skipReason.amountMismatch' },
  { match: 'prefix', key: 'pay_error', i18n: 'dialog.bulkMarkAsPaid.skipReason.payError' }
]

function formatSkipReason(reason: string, t: TFunction): string {
  const matched = SKIP_REASON_I18N.find(entry =>
    entry.match === 'exact' ? entry.key === reason : reason.startsWith(entry.key)
  )

  return matched ? t(matched.i18n) : t('dialog.bulkMarkAsPaid.skipReason.unknown')
}

function PreviewStatusBadge({ row, t }: Readonly<{ row: PreviewTableRow; t: TFunction }>) {
  if (row.status === 'paid') {
    return (
      <Badge variant="positive" icon="approve">
        {t('dialog.bulkMarkAsPaid.preview.status.paid')}
      </Badge>
    )
  }

  const isAlreadyPaid = row.reason === ALREADY_PAID_REASON

  return (
    <Badge variant={isAlreadyPaid ? 'default' : 'warning'} icon={isAlreadyPaid ? 'approve' : undefined}>
      {formatSkipReason(row.reason ?? '', t)}
    </Badge>
  )
}

export function BulkMarkAsPaidModal({ onClose, refresh }: Readonly<BulkMarkAsPaidModalProps>) {
  const { disbursementsService } = useServices()
  const { t } = useTranslation()
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [hasTooManyFilesError, setHasTooManyFilesError] = useState(false)
  const [view, setView] = useState<ModalView>('upload')
  const [csvText, setCsvText] = useState('')
  const [allCsvRows, setAllCsvRows] = useState<ParsedCsvRow[]>([])
  const [validDisbursementIds, setValidDisbursementIds] = useState<string[]>([])
  const [previewSkipped, setPreviewSkipped] = useState<BulkMarkAsPaidSkippedItem[]>([])

  const [{ data: mutationData, error: mutationError }, { mutateAsync: executeBulkMarkAsPaid }] = useMutation(
    ({ csv, dryRun }: { csv: string; dryRun: boolean }) => disbursementsService.bulkMarkAsPaidFromCsv(csv, dryRun)
  )

  const previewColumns = useMemo(
    () => [
      { id: t('dialog.bulkMarkAsPaid.preview.column.orgName') },
      { id: t('dialog.bulkMarkAsPaid.preview.column.amount') },
      { id: t('dialog.bulkMarkAsPaid.preview.column.lastUpdated') },
      { id: t('dialog.bulkMarkAsPaid.preview.column.status') }
    ],
    [t]
  )

  const handleClose = () => {
    setSelectedFiles([])
    setHasTooManyFilesError(false)
    setView('upload')
    setCsvText('')
    setAllCsvRows([])
    setValidDisbursementIds([])
    setPreviewSkipped([])
    onClose()
  }

  const handlePreview = async () => {
    const csv = await selectedFiles[0].text()
    setCsvText(csv)
    setView('previewing')

    try {
      const allRows = parseChequeReconciliationCsv(csv)
      const result = (await executeBulkMarkAsPaid({ csv, dryRun: true })) as BulkMarkAsPaidDryRunResponse

      setAllCsvRows(allRows)
      setValidDisbursementIds(result.data.validDisbursementIds)
      setPreviewSkipped(result.data.skipped)
      setView('preview')
    } catch {
      setView('error')
    }
  }

  const handleConfirm = async () => {
    setView('processing')

    try {
      await executeBulkMarkAsPaid({ csv: csvText, dryRun: false })
      refresh?.()
      setView('success')
    } catch {
      setView('error')
    }
  }

  const handleFileChange = (files: File[]) => {
    setSelectedFiles(files)
    setHasTooManyFilesError(false)
  }

  const handleFileInputChange = (event: ChangeEvent<HTMLDivElement>) => {
    const target = event.target as HTMLInputElement | null

    if (target?.type !== 'file') return
    const fileCount = target.files?.length ?? 0
    setHasTooManyFilesError(fileCount > 1)
  }

  const handleDropCapture = (event: DragEvent<HTMLDivElement>) => {
    const fileCount = event.dataTransfer?.files?.length ?? 0
    setHasTooManyFilesError(fileCount > 1)
  }

  const confirmSkippedItems: BulkMarkAsPaidSkippedItem[] = mutationData?.data?.skipped ?? []
  const succeededCount: number = mutationData?.data?.succeeded ?? 0

  const handleDownloadSkipped = () => {
    if (confirmSkippedItems.length === 0) return

    const csvContent = Papa.unparse({
      fields: ['Disbursement ID', 'Reason'],
      data: confirmSkippedItems.map(item => [item.disbursementId, item.reason])
    })

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const downloadLink = document.createElement('a')
    downloadLink.href = url
    downloadLink.download = SKIPPED_CSV_FILENAME
    downloadLink.click()
    window.URL.revokeObjectURL(url)
  }

  const combinedPreviewRows = useMemo((): PreviewTableRow[] => {
    const skippedMap = new Map(previewSkipped.map(item => [item.disbursementId, item]))
    const validSet = new Set(validDisbursementIds)

    return allCsvRows.reduce<PreviewTableRow[]>((acc, row) => {
      const skippedItem = skippedMap.get(row.disbursementId)

      if (skippedItem) {
        acc.push({
          ...row,
          status: skippedItem.reason === ALREADY_PAID_REASON ? ALREADY_PAID_REASON : 'skipped',
          reason: skippedItem.reason
        })
      } else if (validSet.has(row.disbursementId)) {
        acc.push({ ...row, status: 'paid' })
      }

      return acc
    }, [])
  }, [allCsvRows, validDisbursementIds, previewSkipped])

  const renderUploadModal = () => (
    <ActionModal
      title={t('dialog.bulkMarkAsPaid.title')}
      primaryButtonText={t('button.bulkMarkAsPaid')}
      primaryBtnTestId="bulk-mark-as-paid-submit-button"
      secondaryButtonText={t('button.cancel')}
      handleClose={handleClose}
      handleSubmit={handlePreview}
      loading={view === 'previewing'}
      disabled={selectedFiles.length === 0 || hasTooManyFilesError || view === 'previewing'}
    >
      <Alert variant="info" title={t('dialog.bulkMarkAsPaid.alert.title')}>
        {t('dialog.bulkMarkAsPaid.alert.description')}
      </Alert>
      <Spacer size={4} />
      <div
        data-testid="bulk-mark-as-paid-dropzone-container"
        onChangeCapture={handleFileInputChange}
        onDropCapture={handleDropCapture}
      >
        <Dropzone
          title={t('dialog.bulkMarkAsPaid.dropzoneTitle')}
          description={t('dialog.bulkMarkAsPaid.dropzoneDescription')}
          files={selectedFiles}
          maxFiles={1}
          onChange={handleFileChange}
          accept={[MIME_TYPES.csv]}
          maxSize={MAX_CSV_SIZE_BYTES}
          testId="bulk-mark-as-paid-dropzone"
        />
      </div>
    </ActionModal>
  )

  const renderPreviewModal = () => (
    <ActionModal
      title={t('dialog.bulkMarkAsPaid.title')}
      primaryButtonText={t('button.confirm')}
      primaryBtnTestId="bulk-mark-as-paid-confirm-button"
      secondaryButtonText={t('button.cancel')}
      handleClose={handleClose}
      handleSubmit={handleConfirm}
      loading={view === 'processing'}
      disabled={view === 'processing' || validDisbursementIds.length === 0}
    >
      <Alert variant="info" title={t('dialog.bulkMarkAsPaid.preview.updates')}>
        <Flex direction="column" gap={1}>
          {validDisbursementIds.length > 0 && (
            <span>{t('dialog.bulkMarkAsPaid.preview.markedCount', { count: validDisbursementIds.length })}</span>
          )}
          {previewSkipped.length > 0 && (
            <span>{t('dialog.bulkMarkAsPaid.preview.skippedCount', { count: previewSkipped.length })}</span>
          )}
        </Flex>
      </Alert>
      <Spacer size={4} />
      <div className={styles.previewTableContainer} data-testid="preview-table-container">
        <TableProvider
          columns={previewColumns}
          totalResults={combinedPreviewRows.length}
          isLoading={false}
          illustration="no-results"
          emptyTableTitle={t('dialog.bulkMarkAsPaid.preview.empty')}
        >
          <Table>
            <TableBody>
              {combinedPreviewRows.map(row => (
                <TableRow key={row.disbursementId}>
                  <TableCell>{row.orgName}</TableCell>
                  <TableCell>{row.amount}</TableCell>
                  <TableCell>{row.dateChequeCashed}</TableCell>
                  <TableCell>
                    <PreviewStatusBadge row={row} t={t} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableProvider>
      </div>
    </ActionModal>
  )

  const renderSuccessModal = () => {
    const hasSkipped = confirmSkippedItems.length > 0

    return (
      <AcknowledgeModal
        result="positive"
        title={t('dialog.bulkMarkAsPaid.result.success.title')}
        buttonText={t('button.close')}
        handleClose={handleClose}
      >
        <Text size="small" align="center">
          {t('dialog.bulkMarkAsPaid.result.success.description', { count: succeededCount })}
        </Text>
        {hasSkipped && (
          <>
            <Spacer size={2} />
            <Text size="small" align="center">
              {t('dialog.bulkMarkAsPaid.result.skipped', { count: confirmSkippedItems.length })}
            </Text>
            <Spacer size={2} />
            <Flex justify="center">
              <ButtonText variant="primary" onClick={handleDownloadSkipped}>
                {t('button.downloadSkipped')}
              </ButtonText>
            </Flex>
          </>
        )}
      </AcknowledgeModal>
    )
  }

  const renderErrorModal = () => {
    const errorCode = getApiErrorCode(mutationError)
    const errorDescriptionKey =
      errorCode === ERROR_CODE_NO_VALID_ROWS
        ? 'dialog.bulkMarkAsPaid.result.error.noValidRows'
        : 'dialog.bulkMarkAsPaid.result.error.description'

    return (
      <AcknowledgeModal
        result="negative"
        title={t('dialog.bulkMarkAsPaid.result.error.title')}
        description={t(errorDescriptionKey)}
        buttonText={t('button.close')}
        handleClose={handleClose}
      />
    )
  }

  const modalWidth = view === 'preview' || view === 'processing' ? MODAL_WIDTH_WIDE : MODAL_WIDTH_STANDARD

  return (
    <Modal open onClose={handleClose} width={modalWidth}>
      {(view === 'upload' || view === 'previewing') && renderUploadModal()}
      {(view === 'preview' || view === 'processing') && renderPreviewModal()}
      {view === 'success' && renderSuccessModal()}
      {view === 'error' && renderErrorModal()}
    </Modal>
  )
}
